import type { Option } from '@/modules/common/types/option.type.ts';
import { requirePositiveId } from '@/modules/common/utils/number.util.ts';
import { queryClient } from '@/tanstack-query.ts';
import { SegmentApi } from '@/modules/counterparty/api/segment.api.ts';
import { RegionApi } from '@/modules/counterparty/api/region.api.ts';
import { SegmentQueryOptions } from '@/modules/counterparty/query-options/segment.query-options.ts';
import { RegionQueryOptions } from '@/modules/counterparty/query-options/region.query-options.ts';
import type { DirectoryField } from '@/modules/counterparty/counterparty-master/consts/directory.const.ts';
import type { Segment } from '@/modules/counterparty/types/segment.type.ts';
import type { Region } from '@/modules/counterparty/types/region.type.ts';
import {
  getPendingRegion,
  getPendingSegment,
  isPendingDirectoryId,
  removePendingDirectory,
} from '@/modules/counterparty/counterparty-master/utils/pending-directory.util.ts';
import {
  DirectoryNameDuplicateError,
  findDirectoryNameDuplicate,
} from '@/modules/counterparty/counterparty-master/utils/find-directory-name-duplicate.util.ts';
import { fetchDirectoryList } from '@/modules/counterparty/counterparty-master/utils/fetch-directory-list.util.ts';

const DIRECTORY_UNDEFINED = 'The counterparty region or segment is not defined.';

function toId(value: unknown) {
  return requirePositiveId(value, 'The directory item id is not defined.');
}

function requirePendingName(name: string) {
  const trimmed = name.trim();

  if (!trimmed) {
    throw new Error(DIRECTORY_UNDEFINED);
  }

  return trimmed;
}

function writeDirectoryCache<T extends { id: number }>(queryKey: readonly unknown[], id: number, item: T) {
  queryClient.setQueryData(queryKey, (old: T[] | undefined) => {
    if (!old) {
      return [item];
    }

    return [...old.filter((entry) => Number(entry.id) !== id), item];
  });
}

async function assertUniqueDirectoryName(field: DirectoryField, name: string, signal?: AbortSignal) {
  const list = await fetchDirectoryList(field, signal);

  if (findDirectoryNameDuplicate(list, name)) {
    throw new DirectoryNameDuplicateError(field);
  }
}

export async function resolveMasterDirectoryId(
  field: DirectoryField,
  option: Option<number> | null,
  signal?: AbortSignal
) {
  if (!option || typeof option.code !== 'number') {
    throw new Error(DIRECTORY_UNDEFINED);
  }

  if (!isPendingDirectoryId(option.code)) {
    return toId(option.code);
  }

  if (field === 'segment') {
    const pending = getPendingSegment(option.code);

    if (!pending) {
      throw new Error(DIRECTORY_UNDEFINED);
    }

    const name = requirePendingName(pending.name);
    await assertUniqueDirectoryName(field, name, signal);
    const note = pending.note ?? '';
    const id = toId(await SegmentApi.add(name, note || undefined, signal));
    removePendingDirectory(field, option.code);
    writeDirectoryCache(SegmentQueryOptions.getList().queryKey, id, {
      id,
      name,
      note: note || null,
    } satisfies Segment);
    return id;
  }

  const pending = getPendingRegion(option.code);

  if (!pending) {
    throw new Error(DIRECTORY_UNDEFINED);
  }

  const name = requirePendingName(pending.name);

  if (pending.tzId <= 0) {
    throw new Error('The counterparty region timezone is not defined.');
  }

  await assertUniqueDirectoryName(field, name, signal);

  const id = toId(
    await RegionApi.add(name, pending.tzId, pending.center || undefined, pending.note || undefined, signal)
  );
  removePendingDirectory(field, option.code);
  writeDirectoryCache(RegionQueryOptions.getList().queryKey, id, {
    id,
    name,
    tzId: pending.tzId,
    center: pending.center,
    note: pending.note || null,
  } satisfies Region);
  return id;
}
